import React from "react";
import { Link } from "react-router-dom";
import { Mic, ArrowRight, Play } from "lucide-react";
import { Button } from "../ui/button";
import { Reveal } from "../Reveal";
import LiveDemoModal from "../LiveDemoModal";
import { heroStats, tickerItems } from "../../mock";

const WaveBars = ({ count = 22, className = "" }) => (
  <div className={`flex items-center gap-[3px] ${className}`}>
    {Array.from({ length: count }).map((_, i) => (
      <span
        key={i}
        className="wave-bar w-[3px] rounded-full bg-cyan-400/80"
        style={{ height: `${10 + Math.abs(Math.sin(i * 0.9)) * 26}px`, animationDelay: `${i * 0.08}s` }}
      />
    ))}
  </div>
);

const VoiceOrb = () => (
  <div className="relative mx-auto flex h-[340px] w-[340px] items-center justify-center sm:h-[420px] sm:w-[420px]">
    {/* expanding rings */}
    <div className="ring-expand absolute inset-0 rounded-full border border-cyan-400/30" />
    <div className="ring-expand absolute inset-0 rounded-full border border-cyan-400/20" style={{ animationDelay: "1s" }} />
    <div className="ring-expand absolute inset-0 rounded-full border border-cyan-400/10" style={{ animationDelay: "2s" }} />

    {/* rotating dashed orbit */}
    <div className="spin-slow absolute inset-6 rounded-full border border-dashed border-white/10" />

    {/* core orb */}
    <div className="orb-pulse relative flex h-40 w-40 items-center justify-center rounded-full bg-gradient-to-b from-cyan-400/25 to-sky-500/10 backdrop-blur-xl sm:h-48 sm:w-48">
      <div className="flex h-28 w-28 items-center justify-center rounded-full bg-[#04060a] shadow-[inset_0_0_30px_rgba(34,211,238,0.25)] sm:h-32 sm:w-32">
        <Mic className="h-10 w-10 text-cyan-300" />
      </div>
    </div>

    {/* floating language chips */}
    <div className="float-y absolute -left-2 top-10 rounded-full border border-white/10 bg-white/5 px-3.5 py-1.5 text-xs font-medium text-slate-200 backdrop-blur-md sm:left-0">
      <span className="font-arabic">العربية</span> · Arabic
    </div>
    <div className="float-y absolute -right-2 top-24 rounded-full border border-white/10 bg-white/5 px-3.5 py-1.5 text-xs font-medium text-slate-200 backdrop-blur-md sm:right-0" style={{ animationDelay: "1.2s" }}>
      हिन्दी · Hindi
    </div>
    <div className="float-y absolute bottom-16 left-0 rounded-full border border-white/10 bg-white/5 px-3.5 py-1.5 text-xs font-medium text-slate-200 backdrop-blur-md" style={{ animationDelay: "2s" }}>
      English
    </div>
    <div className="float-y absolute bottom-8 right-2 rounded-full border border-white/10 bg-white/5 px-3.5 py-1.5 text-xs font-medium text-slate-200 backdrop-blur-md" style={{ animationDelay: "0.6s" }}>
      +52 more
    </div>

    <div className="absolute -bottom-4 left-1/2 -translate-x-1/2">
      <WaveBars />
    </div>
  </div>
);

const Hero = () => (
  <section id="platform" className="relative overflow-hidden pt-[72px]">
    <div className="bg-grid absolute inset-0" />
    <div className="bg-radial-glow absolute inset-0" />

    <div className="relative mx-auto max-w-7xl px-5 pb-16 pt-16 lg:px-8 lg:pt-24">
      <div className="grid items-center gap-14 lg:grid-cols-2">
        <div>
          <Reveal>
            <span className="inline-flex items-center gap-2 rounded-full border border-cyan-400/25 bg-cyan-400/5 px-4 py-1.5 text-xs font-medium uppercase tracking-[0.18em] text-cyan-300">
              <span className="blink-dot h-1.5 w-1.5 rounded-full bg-cyan-400" />
              AI-Powered Customer Support for Healthcare
            </span>
          </Reveal>

          <Reveal delay={120}>
            <h1 className="font-display mt-6 text-4xl font-bold leading-[1.08] text-white sm:text-5xl lg:text-[3.6rem]">
              Voice AI Agents that answer <span className="text-cyan-400">every patient call</span>
            </h1>
          </Reveal>

          <Reveal delay={220}>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-slate-400">
              Arkision deploys autonomous, multilingual voice agents for hospitals — booking appointments,
              resolving queries and engaging patients 24/7, while your data stays inside your country.
            </p>
          </Reveal>

          <Reveal delay={320}>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <LiveDemoModal
                trigger={
                  <Button
                    size="lg"
                    className="group rounded-full bg-cyan-400 px-7 font-semibold text-slate-950 transition-colors duration-200 hover:bg-cyan-300"
                  >
                    <Play className="mr-2 h-4 w-4 transition-transform duration-200 group-hover:scale-110" />
                    Try the Live Demo
                  </Button>
                }
              />
              <Link to="/industries/healthcare">
                <Button
                  size="lg"
                  variant="outline"
                  className="group rounded-full border-white/15 bg-transparent px-7 font-semibold text-white transition-colors duration-200 hover:border-cyan-400/50 hover:bg-cyan-400/5 hover:text-cyan-200"
                >
                  Healthcare Solutions
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                </Button>
              </Link>
            </div>
          </Reveal>

          <Reveal delay={420}>
            <div className="mt-12 grid grid-cols-2 gap-x-8 gap-y-6 sm:grid-cols-4">
              {heroStats.map((s) => (
                <div key={s.label}>
                  <div className="font-display text-2xl font-bold text-white sm:text-3xl">{s.value}</div>
                  <div className="mt-1 text-xs leading-snug text-slate-500">{s.label}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>

        <Reveal delay={250} className="hidden lg:block">
          <VoiceOrb />
        </Reveal>
      </div>
    </div>

    {/* ticker */}
    <div className="relative border-y border-white/10 bg-white/[0.02] py-3.5">
      <div className="overflow-hidden">
        <div className="marquee-track flex w-max items-center gap-10">
          {[...tickerItems, ...tickerItems].map((t, i) => (
            <span key={i} className="flex items-center gap-10 whitespace-nowrap text-sm font-medium text-slate-400">
              {t}
              <span className="h-1.5 w-1.5 rounded-full bg-cyan-400/60" />
            </span>
          ))}
        </div>
      </div>
    </div>
  </section>
);

export default Hero;
